import { motion } from "framer-motion";
import { useLang } from "../i18n/LangContext";
import { MaskLines, Eyebrow } from "../components/Reveal";
import { PrimaryCTA, GhostCTA } from "../components/Buttons";
import { BulbFallback } from "../components/BulbFallback";
import { hasWebGL, scrollToId, EASE } from "../lib/util";

const fade = (delay) => ({ initial: { opacity: 0, y: 18 }, transition: { duration: 1.1, ease: EASE, delay } });

export const Hero = ({ ready }) => {
  const { t } = useLang();
  const show = (d) => ({ ...fade(d), animate: ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 } });
  return (
    <section id="home" data-core data-testid="hero-section" className="relative flex min-h-[100svh] flex-col overflow-hidden">
      {!hasWebGL() && <BulbFallback className="absolute left-1/2 top-[11%] h-[30vh] w-[22vh] -translate-x-1/2 md:end-[14%] md:left-auto md:top-[20%] md:h-[60vh] md:w-[44vh] md:translate-x-0" />}
      <div className="relative mx-auto flex w-full max-w-[1440px] flex-1 items-end px-6 pb-24 pt-[38svh] md:items-center md:pt-36 lg:px-12">
        <div className="max-w-[44rem]">
          <motion.div {...show(0.1)}>
            <Eyebrow>{t.tagline}</Eyebrow>
          </motion.div>
          <MaskLines
            as="h1"
            testId="hero-headline"
            start={ready}
            delay={0.2}
            lines={t.hero.lines}
            className="display-xl mt-6 font-display text-noor-pearl"
            lineClassName="italic-accent"
          />
          <motion.p {...show(0.75)} className="mt-6 font-display text-xl text-noor-champagne md:text-2xl" data-testid="hero-subheadline">
            {t.hero.sub}
          </motion.p>
          <motion.p {...show(0.9)} className="mt-5 max-w-xl text-[15px] leading-relaxed text-noor-mist md:text-base">
            {t.hero.desc}
          </motion.p>
          <motion.div {...show(1.05)} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <PrimaryCTA testId="hero-primary-cta">{t.hero.cta1}</PrimaryCTA>
            <GhostCTA testId="hero-secondary-cta" onClick={() => scrollToId("clarity")}>{t.hero.cta2}</GhostCTA>
          </motion.div>
          <motion.p {...show(1.2)} className="mt-5 flex items-center gap-2 text-xs text-noor-mist">
            <span className="h-1.5 w-1.5 rounded-full bg-noor-champagne shadow-[0_0_12px_#D8BB86]" />
            {t.hero.free}
          </motion.p>
        </div>
      </div>
      <motion.div {...show(1.4)} className="absolute inset-x-0 bottom-0 mx-auto flex max-w-[1440px] items-end justify-between px-6 pb-7 lg:px-12">
        <button data-testid="hero-scroll-indicator" onClick={() => scrollToId("clarity")} className="group flex items-center gap-3 text-xs text-noor-mist">
          <span className="scroll-pill"><span className="scroll-dot" /></span>
          <span className="track">{t.hero.scroll}</span>
        </button>
        <span className="hidden text-xs text-noor-mist/80 md:block">
          <span className="font-brand text-noor-champagne">N°01</span> — {t.hero.label}
        </span>
      </motion.div>
    </section>
  );
};
